import { thicknessOptions } from '../components/Thickness/thicknessOptions';

const SELECT_STROKE = 'rgba(63, 152, 249, 1)';
const SELECT_FILL = 'rgba(63, 152, 249, 0.05)';

const isRgba = (color) => typeof color === 'string' && color.includes('rgba(');

export const resetObjectStyle = (obj) => {
  if (!obj.previousStroke && !obj.previousFill) return;

  if (obj.objectType != 'freehand') {
    obj.set({
      stroke: obj.previousStroke,
      fill: obj.previousFill,
    });
  } else {
    obj.set({
      stroke: obj.previousStroke,
    });
  }
};

export const selectObjectStyle = (obj) => {
  obj.set({
    previousStroke: obj.stroke,
    previousFill: obj.fill,
  });

  if (obj.objectType != 'freehand') {
    obj.set({
      stroke: SELECT_STROKE,
      fill: SELECT_FILL,
    });
  } else {
    obj.set({
      stroke: SELECT_STROKE,
    });
  }
};

export const colorObjectStyle = (obj, newColor) => {
  const skipFillTypes = ['cloud', 'freehand'];

  const getFillOpacity = (type) =>
    ['ellipse', 'polygon', 'rect'].includes(type) ? 0.05 : 0.3;

  const getColor = (oldColor, type) =>
    isRgba(oldColor) ? hexToRgba(newColor, getFillOpacity(type)) : newColor;

  if (obj.type === 'activeSelection') {
    obj.getObjects().forEach((child) => {
      const stroke = getColor(child.stroke, child.type);
      const setObj = { stroke };

      if (!skipFillTypes.includes(child.objectType)) {
        setObj.fill = getColor(child.fill, child.type);
      }

      child.set(setObj);
      child.setCoords?.();
    });
  } else {
    const previousStroke = getColor(obj.previousStroke, obj.type);
    const setObj = { previousStroke };

    if (!skipFillTypes.includes(obj.objectType)) {
      setObj.previousFill = getColor(obj.previousFill, obj.type);
    }

    obj.set(setObj);
    obj.setCoords?.();
  }
};

export const setObjectThickness = (obj, selectedThickness) => {
  if (!obj) return;

  const applyThickness = (target) => {
    const isFreehand = target.objectType === 'freehand';
    const isHighlight = isFreehand && isRgba(target.previousStroke);
    const thickness = getThicknessPx(
      selectedThickness,
      isHighlight ? 'highlighter' : undefined
    );

    target.set({ strokeWidth: thickness });
    target.setCoords?.();
  };

  if (obj.type === 'activeSelection') {
    obj.getObjects().forEach(applyThickness);
  } else {
    applyThickness(obj);
  }
};

export function overrideContainsPoint(obj, canvas) {
  if (!obj || !canvas) return;

  if (!obj._originalContainsPoint) {
    obj._originalContainsPoint = obj.containsPoint;
  }
  obj.containsPoint = function (point) {
    const isTransparent = canvas.isTargetTransparent(this, point.x, point.y);
    return !isTransparent;
  };
}

export function resetContainsPoint(obj) {
  if (!obj) return;

  if (obj._originalContainsPoint) {
    obj.containsPoint = obj._originalContainsPoint;
    delete obj._originalContainsPoint;
  } else {
    delete obj.containsPoint;
  }
}

export const addControlPointsToLine = (line, canvas, viewer, plan) => {
  if (!line || line.objectType !== 'line') return;

  const zoom = viewer.viewport.getZoom();
  const pixelToMmRatio = 7.874;
  const controlPointRatio = 14;

  const size = (pixelToMmRatio / zoom) * controlPointRatio;

  const createCP = (x, y, updateFn) => {
    const cp = new fabric.Rect({
      left: x - size / 2,
      top: y - size / 2,
      width: size,
      height: size,
      fill: 'rgba(255,0,0,0.05)',
      stroke: '#22a2f8',
      strokeWidth: size / 10,
      hasBorders: false,
      hasControls: false,
      objectCaching: false,
      selectable: true,
      objectType: 'controlpoint',
    });

    cp.on('moving', function () {
      const bounded = {
        x: Math.max(0, Math.min(plan.width, this.left + this.width / 2)),
        y: Math.max(0, Math.min(plan.height, this.top + this.height / 2)),
      };

      this.set({
        left: bounded.x - this.width / 2,
        top: bounded.y - this.height / 2,
      });

      updateFn(bounded);
      updateLinePosition(line);
      line.setCoords();
      canvas.requestRenderAll();
    });

    return cp;
  };

  const cp1 = createCP(line.x1, line.y1, (p) => line.set({ x1: p.x, y1: p.y }));
  const cp2 = createCP(line.x2, line.y2, (p) => line.set({ x2: p.x, y2: p.y }));

  canvas.add(cp1, cp2);
  cp1.bringToFront();
  cp2.bringToFront();

  line.controlPoints = [cp1, cp2];
};

const updateLinePosition = (line) => {
  line.set({
    left: Math.min(line.x1, line.x2),
    top: Math.min(line.y1, line.y2),
    startLeft: line.left,
    startTop: line.top,
  });
};

export const removeControlPoints = (canvas) => {
  const controlPoints = canvas
    .getObjects()
    .filter((obj) => obj.objectType === 'controlpoint');

  controlPoints.forEach((cp) => {
    canvas.remove(cp);
  });

  canvas.getObjects().forEach((obj) => {
    obj.set('controlPoints', []);
  });

  canvas.requestRenderAll();
};

export const disableSelectionControls = (selection) => {
  if (!selection || selection.type !== 'activeSelection') return;

  selection.set({
    hasControls: false,
    hasBorders: true,
    lockRotation: true,
    lockScalingX: true,
    lockScalingY: true,
    lockMovementX: true,
    lockMovementY: true,
    selectable: true,
    // evented: true,
  });

  selection.setCoords();
};

export const hexToRgba = (hex, alpha = 1) => {
  const bigint = parseInt(hex.replace('#', ''), 16);
  const r = (bigint >> 16) & 255;
  const g = (bigint >> 8) & 255;
  const b = bigint & 255;
  return `rgba(${r}, ${g}, ${b}, ${alpha})`;
};

export const findTaskObjectOnCanvas = (canvas, taskID) => {
  if (!canvas || !taskID) return null;

  const objects = canvas.getObjects();

  const taskObj = objects.find((obj) => {
    return obj.objectType === 'task' && obj.taskID == taskID;
  });

  return taskObj || null;
};

export function getThicknessPx(selectedThickness, activeTool) {
  const thicknessObj = thicknessOptions.find(
    (opt) => opt.key === selectedThickness
  );
  if (!thicknessObj) return 1;

  if (activeTool === 'highlighter') {
    return thicknessObj.highlighterPx;
  } else {
    return thicknessObj.pencilPx;
  }
}
